import React from 'react';
import {
  actionIdSchema,
  NATIVE_ANALYTICS_LABEL_PROP,
  type ActionId,
  type InputMethod,
} from '@galapagos/product-analytics';

export interface AnalyticsActivation {
  actionId: ActionId;
  inputMethod: InputMethod;
  /**
   * Bounded discriminator from a closed set (e.g. a tab key) for controls that
   * share one action id across sibling instances. Registered as the
   * `element_key` action property; never free text.
   */
  elementKey?: string;
}

export type CaptureAnalyticsActivation = (activation: AnalyticsActivation) => void;

const noopCapture: CaptureAnalyticsActivation = () => {};
const AnalyticsCaptureContext = React.createContext<CaptureAnalyticsActivation>(noopCapture);
/**
 * The app's real capture, preserved across suppression.
 *
 * Suppression overrides the ACTIVE capture; this one keeps a handle on what was
 * suppressed so a subtree that genuinely owns no tool analytics can restore it.
 * Without it, suppression is one-way and anything inside a GenUI host is
 * permanently unable to emit — which is a silent hole rather than a policy,
 * because the declaration and the delivery then disagree with nothing to catch it.
 */
const AnalyticsCaptureSourceContext = React.createContext<CaptureAnalyticsActivation>(noopCapture);
const AnalyticsDelegationSuppressionContext = React.createContext(false);
const parsedActionIds = new Map<string, ActionId | null>();

function resolveActionId(actionId: string | undefined): ActionId | undefined {
  if (!actionId) return undefined;
  const cached = parsedActionIds.get(actionId);
  if (cached !== undefined) return cached ?? undefined;

  const result = actionIdSchema.safeParse(actionId);
  const parsed = result.success ? result.data : null;
  parsedActionIds.set(actionId, parsed);
  return parsed ?? undefined;
}

export function AnalyticsActionProvider({
  capture,
  children,
}: {
  capture: CaptureAnalyticsActivation;
  children: React.ReactNode;
}) {
  return (
    <AnalyticsCaptureSourceContext.Provider value={capture}>
      <AnalyticsCaptureContext.Provider value={capture}>
        {children}
      </AnalyticsCaptureContext.Provider>
    </AnalyticsCaptureSourceContext.Provider>
  );
}

/** GenUI hosts own tool analytics, so their rendered primitives suppress generic activation capture. */
export function AnalyticsActionSuppressionProvider({ children }: { children: React.ReactNode }) {
  return (
    <AnalyticsDelegationSuppressionContext.Provider value>
      <AnalyticsCaptureContext.Provider value={noopCapture}>
        {children}
      </AnalyticsCaptureContext.Provider>
    </AnalyticsDelegationSuppressionContext.Provider>
  );
}

/**
 * Restores the app's real capture inside a suppressed GenUI subtree.
 *
 * The suppression above exists because a `ToolCall` emits its own tool
 * analytics, so letting the rendered primitive ALSO emit a generic activation
 * would double-count. That reasoning does not extend to a control the host
 * resolves itself: it runs no tool, so it has no tool analytics, and under
 * blanket suppression it emits nothing at all. Its declared action id would then
 * sit in the catalogue as a row that can never fire — the declaration and the
 * delivery disagreeing, one layer below where anyone looks.
 *
 * Scoped deliberately: wrap only the host-resolved control, never the host's
 * whole tree, or the double-count this suppression prevents comes back.
 */
export function AnalyticsActionRestoreProvider({ children }: { children: React.ReactNode }) {
  const capture = React.useContext(AnalyticsCaptureSourceContext);
  return (
    <AnalyticsDelegationSuppressionContext.Provider value={false}>
      <AnalyticsCaptureContext.Provider value={capture}>
        {children}
      </AnalyticsCaptureContext.Provider>
    </AnalyticsDelegationSuppressionContext.Provider>
  );
}

export function useAnalyticsActivation(
  actionId: string | undefined,
  onActivate: (() => void) | undefined,
  disabled: boolean,
  inputMethod: InputMethod,
  elementKey?: string,
): (() => void) | undefined {
  return useAnalyticsCallback(actionId, onActivate, disabled, inputMethod, elementKey);
}

/**
 * Wraps an accessibility-action handler so each assistive-technology
 * activation captures the literal id of its gesture twin (AT parity —
 * a long-press or close gesture and its announced AT action are the same
 * semantic interaction, so they share one action id). Returns undefined
 * while disabled, keeping the control fully inert.
 */
export function useAnalyticsAccessibilityActions<E extends { nativeEvent: { actionName: string } }>(
  actionIds: Record<string, string> | undefined,
  onAction: ((event: E) => void) | undefined,
  disabled: boolean,
): ((event: E) => void) | undefined {
  const capture = React.useContext(AnalyticsCaptureContext);
  if (disabled || !onAction) return undefined;
  return (event) => {
    const parsed = resolveActionId(actionIds?.[event.nativeEvent.actionName]);
    if (parsed) capture({ actionId: parsed, inputMethod: 'programmatic' });
    onAction(event);
  };
}

/** Web apps emit through one root delegated listener; GenUI suppression removes the marker. */
export function useDelegatedAnalyticsAction(actionId: string | undefined): ActionId | undefined {
  const suppressDelegated = React.useContext(AnalyticsDelegationSuppressionContext);
  if (suppressDelegated) return undefined;
  return resolveActionId(actionId);
}

export function useAnalyticsCallback<Args extends unknown[], Result>(
  actionId: string | undefined,
  callback: ((...args: Args) => Result) | undefined,
  disabled: boolean,
  inputMethod: InputMethod,
  elementKey?: string,
): ((...args: Args) => Result | undefined) | undefined {
  const capture = React.useContext(AnalyticsCaptureContext);
  const parsedActionId = resolveActionId(actionId);
  if (disabled || !callback) return undefined;
  return (...args: Args) => {
    if (parsedActionId) {
      capture({
        actionId: parsedActionId,
        inputMethod,
        ...(elementKey !== undefined ? { elementKey } : {}),
      });
    }
    return callback?.(...args);
  };
}

/** Attach provider-neutral action metadata to the actual React Native touch target. */
export function analyticsLabelProps(actionId: string | undefined): {
  [NATIVE_ANALYTICS_LABEL_PROP]?: ActionId;
} {
  const resolved = resolveActionId(actionId);
  return resolved ? { [NATIVE_ANALYTICS_LABEL_PROP]: resolved } : {};
}
